import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, X, Users, KeyRound, Activity, Power, PowerOff, Edit, Car } from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const PERMISSIONS = [
  { key: "bookings", label: "Bookings (create / edit / cancel)" },
  { key: "vehicles", label: "Vehicles (add / edit / retire)" },
  { key: "incidents", label: "Incidents (create / resolve / notify)" },
  { key: "reminders", label: "Reminders (run / send / skip)" },
  { key: "verification_requests", label: "Verification requests (respond)" },
  { key: "reports", label: "Reports & audit log" },
  { key: "settings", label: "Company settings & day rates" },
  { key: "communications", label: "Message templates & automation" },
  { key: "drivers", label: "Driver operations (assign / manage drivers)" },
  { key: "finances", label: "Finance & compensation records" },
];

// Finance & Books capabilities. Deliberately separate from the list above:
// each one is granted explicitly, and the legacy "finances" group keeps
// meaning exactly what it meant before Books existed.
const FINANCE_CAPABILITIES = [
  { key: "finance.view", label: "See Finance & Books (reports, journals)" },
  { key: "finance.record_payment", label: "Record payments, advances and transfers" },
  { key: "finance.record_expense", label: "Record expenses and vendor bills" },
  { key: "finance.deposits.collect", label: "Collect security deposits" },
  { key: "finance.deposits.refund", label: "Refund security deposits" },
  { key: "finance.deposits.apply", label: "Apply deposits to charges" },
  { key: "finance.post", label: "Post rental and customer charges" },
  { key: "finance.correct", label: "Correct posted entries (reversals)" },
  { key: "finance.close_period", label: "Close and reopen months" },
  { key: "finance.export", label: "Export accounting records" },
  { key: "finance.configure", label: "Set up Finance & Books" },
];

// Legal Readiness capabilities (backend/client_legal/rbac.py). None of them
// can verify anything: verification is done by ConfirmPH reviewers only.
const DRIVER_CAPABILITIES = [
  { key: "drivers.view", label: "See drivers and their readiness" },
  { key: "drivers.manage", label: "Edit driver profiles, groups and employment status" },
  { key: "drivers.assign", label: "Assign and reassign drivers to bookings" },
  { key: "drivers.availability.manage", label: "Manage driver schedules and time off" },
  { key: "drivers.compliance.view", label: "See driver legal readiness" },
  { key: "drivers.compliance.manage", label: "Record employer verification of driver licences" },
  { key: "drivers.documents.view", label: "Open driver documents" },
  { key: "drivers.safety.manage", label: "Open and resolve driver safety holds" },
  { key: "drivers.dispatch.acknowledge", label: "Acknowledge review items when a trip starts" },
  { key: "drivers.settings.manage", label: "Change driver service settings and pricing" },
  { key: "drivers.reports.view", label: "See driver service reports" },
];

const RENTER_DRIVER_CAPABILITIES = [
  { key: "renter_drivers.view", label: "See renter drivers and self-drive release status" },
  { key: "renter_drivers.manage", label: "Add, invite and remove renter drivers" },
  { key: "renter_drivers.document_view", label: "Open renter driver licence documents" },
  { key: "renter_drivers.document_review", label: "Review renter driver licences and review items" },
  { key: "renter_drivers.release_check", label: "Record pickup licence and identity checks" },
  { key: "renter_drivers.policy_configure", label: "Change self-drive driver policy" },
  { key: "renter_drivers.policy_override", label: "Approve business-policy exceptions for a driver" },
];

// Task 7: deciding that a customer owes money, applying a deposit, recording
// a refund and closing a rental are separate from handing a vehicle over.
const RENTAL_LIFECYCLE_CAPABILITIES = [
  { key: "rental.view", label: "See the rental lifecycle and its money state" },
  { key: "rental.handover", label: "Hand the vehicle over to the customer" },
  { key: "rental.return", label: "Record the return and return inspection" },
  { key: "rental.damage_manage", label: "Open and run damage cases" },
  { key: "rental.charge_propose", label: "Propose a customer charge" },
  { key: "rental.charge_establish", label: "Establish or reverse a customer charge" },
  { key: "rental.deposit_apply", label: "Apply a security deposit to an established charge" },
  { key: "rental.deposit_refund", label: "Record a security deposit refund" },
  { key: "rental.dispute_manage", label: "Handle customer disputes" },
  { key: "rental.claim_manage", label: "Handle insurance claims" },
  { key: "rental.close", label: "Close or reopen a rental financially" },
  { key: "rental.policy_configure", label: "Change the release payment policy" },
];

const COMPLIANCE_CAPABILITIES = [
  { key: "compliance.view", label: "See Legal Readiness (statuses and guidance)" },
  { key: "compliance.manage", label: "Record business legal requirements and operating facts" },
  { key: "compliance.documents.view", label: "Open compliance documents" },
  { key: "compliance.documents.upload", label: "Upload compliance evidence" },
  { key: "compliance.vehicle.view", label: "See vehicle legal readiness" },
  { key: "compliance.vehicle.manage", label: "Record vehicle registration, ownership and authority" },
  { key: "compliance.insurance.view", label: "See vehicle insurance records" },
  { key: "compliance.insurance.manage", label: "Record vehicle insurance" },
  { key: "compliance.export", label: "Export the Legal Readiness report" },
  { key: "compliance.tax.view", label: "See BIR and books readiness only (accountant)" },
];

const DRIVER_STATUSES = ["active", "inactive", "suspended", "unavailable"];

export default function Employees() {
  const { user, company } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [perfFor, setPerfFor] = useState(null);
  const [hideDeactivated, setHideDeactivated] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/employees")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900" data-testid="employees-no-access">
        Only the business owner can manage employees.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Employees</h1>
          <p className="text-slate-600 mt-1">Add staff to your business, set their permissions, and track activity.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="emp-hide-deactivated">
            <input
              type="checkbox" checked={hideDeactivated}
              onChange={(e) => setHideDeactivated(e.target.checked)}
            />
            Hide deactivated
          </label>
          {company?.driver_services_enabled && (
            <Link to="/app/drivers" data-testid="employees-manage-drivers"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 font-medium text-sm">
              <Car className="w-4 h-4" /> Driver Administration
            </Link>
          )}
          <button
            data-testid="employees-add-btn"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium"
          >
            <Plus className="w-4 h-4" /> Add employee
          </button>
        </div>
      </div>

      {loading && <div className="text-slate-500">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="employees-empty">
          <Users className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-slate-600">No employees yet. Add your first one to delegate work.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items
          .filter((e) => !hideDeactivated || e.active !== false)
          .map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`employee-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold truncate">{e.name}</h3>
                {e.title && (
                  <p className="text-xs text-slate-600 truncate" data-testid={`emp-title-${e.id}`}>{e.title}</p>
                )}
                <p className="text-xs text-slate-500 font-mono truncate" data-testid={`emp-login-${e.id}`}>
                  {e.username || e.email}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${
                  e.active === false
                    ? "bg-stone-100 text-stone-700 border-stone-200"
                    : "bg-emerald-100 text-emerald-800 border-emerald-200"
                }`}>
                  {e.active === false ? "Deactivated" : "Active"}
                </span>
                {e.is_driver && (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium border bg-sky-50 text-sky-800 border-sky-200"
                        data-testid={`emp-driver-badge-${e.id}`}>
                    <Car className="w-3 h-3" /> Driver ({e.driver_status || "active"})
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-600 space-y-1.5">
              {(e.phone || e.hire_date) && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-600" data-testid={`emp-contact-${e.id}`}>
                  {e.phone && <span><span className="text-slate-500">Phone:</span> <span className="font-mono">{e.phone}</span></span>}
                  {e.hire_date && <span><span className="text-slate-500">Joined:</span> {String(e.hire_date).slice(0, 10)}</span>}
                </div>
              )}
              <div>
                <span className="font-semibold uppercase tracking-[0.18em] text-slate-500">Permissions</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(e.permissions || []).length === 0 && <span className="text-slate-400">None</span>}
                  {(e.permissions || []).map((p) => (
                    <span key={p} className="px-2 py-0.5 bg-slate-100 rounded-full">{p}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => { setEditing(e); setShowForm(true); }}
                      data-testid={`emp-edit-${e.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 hover:bg-slate-50">
                <Edit className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => setPerfFor(e)}
                      data-testid={`emp-perf-${e.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-300 hover:bg-slate-50">
                <Activity className="w-3 h-3" /> Work Summary
              </button>
              {e.active !== false ? (
                <button onClick={async () => {
                  if (!window.confirm(`Deactivate ${e.name}? They won't be able to log in.`)) return;
                  await api.delete(`/employees/${e.id}`);
                  toast.success("Employee deactivated");
                  load();
                }}
                  data-testid={`emp-deactivate-${e.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50">
                  <PowerOff className="w-3 h-3" /> Deactivate
                </button>
              ) : (
                <button onClick={async () => {
                  await api.put(`/employees/${e.id}`, { active: true });
                  toast.success("Employee reactivated");
                  load();
                }}
                  data-testid={`emp-activate-${e.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Power className="w-3 h-3" /> Reactivate
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <EmployeeFormModal
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {perfFor && <PerformanceModal employee={perfFor} onClose={() => setPerfFor(null)} />}
    </div>
  );
}

function EmployeeFormModal({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState(initial ? {
    name: initial.name || "",
    title: initial.title || "",
    email: initial.email || "",
    username: initial.username || "",
    phone: initial.phone || "",
    hire_date: (initial.hire_date || "").slice(0, 10),
    password: "",
    permissions: initial.permissions || [],
    is_driver: !!initial.is_driver,
    driver_status: initial.driver_status || "active",
    driver_display_name: initial.driver_display_name || "",
    driver_phone: initial.driver_phone || "",
    license_number: initial.license_number || "",
    license_class: initial.license_class || "",
    license_expiry: (initial.license_expiry || "").slice(0, 10),
    driver_notes: initial.driver_notes || "",
  } : {
    name: "", title: "", email: "", username: "", phone: "", hire_date: "",
    password: "", permissions: ["bookings"],
    is_driver: false, driver_status: "active", driver_display_name: "",
    driver_phone: "", license_number: "", license_class: "",
    license_expiry: "", driver_notes: "",
  });
  const [saving, setSaving] = useState(false);
  const { panelRef } = useAccessibleOverlay(true, onClose);

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const driver = form.is_driver ? {
        is_driver: true,
        driver_status: form.driver_status,
        driver_display_name: form.driver_display_name || null,
        driver_phone: form.driver_phone || null,
        license_number: form.license_number || null,
        license_class: form.license_class || null,
        license_expiry: form.license_expiry || null,
        driver_notes: form.driver_notes || null,
      } : (editing && initial.is_driver ? { is_driver: false } : null);
      if (editing) {
        const payload = {
          name: form.name, phone: form.phone, permissions: form.permissions,
          title: form.title, hire_date: form.hire_date || null,
        };
        if (form.password) payload.password = form.password;
        if (driver) payload.driver = driver;
        await api.put(`/employees/${initial.id}`, payload);
        toast.success("Employee updated");
      } else {
        const payload = {
          name: form.name, phone: form.phone, password: form.password,
          permissions: form.permissions,
          title: form.title || null,
          hire_date: form.hire_date || null,
        };
        if (form.email?.trim()) payload.email = form.email.trim();
        if (form.username?.trim()) payload.username = form.username.trim();
        if (!payload.email && !payload.username) {
          toast.error("Please provide either an email or a username");
          setSaving(false);
          return;
        }
        if (driver) payload.driver = driver;
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="employee-form-modal">
      <div className="min-h-full flex items-center justify-center p-4">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="employee-form-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full p-6 my-4">
        <div className="flex items-center justify-between">
          <h2 id="employee-form-title" className="font-display text-xl font-semibold">{editing ? `Edit ${initial.name}` : "Add employee"}</h2>
          <button type="button" onClick={onClose} aria-label="Close employee editor" className="p-1 rounded-md hover:bg-slate-100" data-testid="emp-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-4">
          <Field label="Full name" required testId="emp-form-name" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} className="sm:col-span-2" />

          <Field label="Job title (optional)" testId="emp-form-title"
                 value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })}
                 placeholder="Booking Staff / Vehicle Checker / Manager"
                 className="sm:col-span-2" />

          {!editing && (
            <>
              <Field label="Email (optional)" type="email" testId="emp-form-email"
                     value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="sarah@abccarrental.com" />
              <Field label="Username (optional)" testId="emp-form-username"
                     value={form.username}
                     onChange={(e) => setForm({ ...form, username: e.target.value })}
                     placeholder="sarah.sameth" />
            </>
          )}
          {editing && (
            <>
              <Info label="Email" value={initial.email} />
              <Info label="Username" value={initial.username || "-"} />
            </>
          )}

          <Field label="Phone (optional)" testId="emp-form-phone"
                 value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Hire date (optional)" type="date" testId="emp-form-hire-date"
                 value={form.hire_date}
                 onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
          <Field
            label={editing ? "New password (leave blank to keep current)" : "Password (min 6 chars)"}
            type="password"
            required={!editing}
            testId="emp-form-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />

          <div className="sm:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Permissions</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-permissions">
              {PERMISSIONS.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              <KeyRound className="w-3 h-3 inline mr-1" />
              Employees only see and act on what you allow here. Owner-only screens (like this Employees page) stay hidden.
            </p>

            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Finance &amp; Books
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-finance-permissions">
              {FINANCE_CAPABILITIES.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Money powers are separate on purpose: collecting a deposit, refunding one and correcting a posted
              entry are different levels of trust.
            </p>

            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Driver service
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-driver-permissions">
              {DRIVER_CAPABILITIES.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">Driver pay stays behind the Finance permission. None of these shows compensation.</p>

            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Self-drive renter drivers
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-renter-driver-permissions">
              {RENTER_DRIVER_CAPABILITIES.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Rental settlement
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-rental-lifecycle-permissions">
              {RENTAL_LIFECYCLE_CAPABILITIES.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">Establishing a charge, applying a deposit and recording a refund are deliberately separate from handing a vehicle over.</p>

            <div className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Legal Readiness
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="emp-compliance-permissions">
              {COMPLIANCE_CAPABILITIES.map((p) => (
                <label key={p.key} className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`emp-perm-${p.key}`}
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-slate-700">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2 rounded-lg border border-slate-200 p-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="emp-is-driver">
              <input type="checkbox" checked={form.is_driver}
                     onChange={(e) => setForm({ ...form, is_driver: e.target.checked })} />
              <span className="text-sm font-medium text-slate-700">This person is a driver</span>
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Not exclusive with ordinary staff roles: someone can be staff-only, driver-only, or both.
            </p>
            {form.is_driver && (
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <label>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Driver status</span>
                  <select data-testid="emp-driver-status" value={form.driver_status}
                          onChange={(e) => setForm({ ...form, driver_status: e.target.value })}
                          className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300">
                    {DRIVER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <Field label="Display name (optional)" testId="emp-driver-display-name"
                       value={form.driver_display_name}
                       onChange={(e) => setForm({ ...form, driver_display_name: e.target.value })}
                       placeholder="Shown to customers instead of full name" />
                <Field label="Driver contact phone" testId="emp-driver-phone"
                       value={form.driver_phone}
                       onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} />
                <Field label="License number" testId="emp-driver-license-number"
                       value={form.license_number}
                       onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
                <Field label="License class" testId="emp-driver-license-class"
                       value={form.license_class}
                       onChange={(e) => setForm({ ...form, license_class: e.target.value })}
                       placeholder="e.g. Non-Pro / Pro" />
                <Field label="License expiry" type="date" testId="emp-driver-license-expiry"
                       value={form.license_expiry}
                       onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} />
                <label className="sm:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Internal notes</span>
                  <textarea data-testid="emp-driver-notes" value={form.driver_notes} rows={2}
                            onChange={(e) => setForm({ ...form, driver_notes: e.target.value })}
                            className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300" />
                </label>
                <p className="sm:col-span-2 text-xs text-slate-500">
                  Compensation, documents, and availability are managed from Driver Administration after saving.
                </p>
              </div>
            )}
          </div>

          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="emp-form-submit"
                    className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create employee")}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function PerformanceModal({ employee, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { panelRef } = useAccessibleOverlay(true, onClose);

  useEffect(() => {
    api.get(`/employees/${employee.id}/performance`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [employee.id]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="emp-perf-modal">
      <div className="min-h-full flex items-center justify-center p-4">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="employee-performance-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6 my-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 id="employee-performance-title" className="font-display text-xl font-semibold">{employee.name}</h2>
            <p className="text-xs text-slate-500">{employee.username || employee.email}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close employee performance" className="p-1 rounded-md hover:bg-slate-100" data-testid="emp-perf-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {loading && <div className="mt-6 text-slate-500">Loading…</div>}
        {data && (
          <div className="mt-5 grid grid-cols-2 gap-3" data-testid="emp-perf-grid">
            <Metric label="Bookings created" value={data.metrics.bookings_created} />
            <Metric label="Bookings completed" value={data.metrics.bookings_completed} />
            <Metric label="Bookings cancelled" value={data.metrics.bookings_cancelled} />
            <Metric label="Incidents resolved" value={data.metrics.incidents_resolved} />
            <Metric label="Verification requests" value={data.metrics.verification_requests_handled} />
            <Metric label="Vehicles added" value={data.metrics.vehicles_added} />
          </div>
        )}
        <p className="mt-4 text-xs text-slate-500">
          Privacy-friendly aggregates only. We don't track every keystroke or per-action timestamp.
        </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, className = "", placeholder }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input
        data-testid={testId} type={type} required={required} value={value} onChange={onChange} placeholder={placeholder}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none"
      />
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <div className="mt-1.5 px-3.5 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700">{value || "-"}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-display font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}
